import { holds } from '../../lib/permissionMatch.ts';
import { payable } from '../../lib/proformaState.ts';
import { type InvoiceGateFacts, invoiceActionGates } from './invoiceActions.ts';

/**
 * The Actions card of the document page (spec 9.5): every action the matrix names for the
 * document's state, grouped Payment / Document / Change. An action the user cannot take now is
 * still shown, disabled, with the reason and the way forward. Built on `invoiceActionGates`, so
 * the page offers exactly what the server accepts.
 */
export type ActionKey =
  | 'recordPayment'
  | 'addReport'
  | 'settle'
  | 'send'
  | 'download'
  | 'mondayCreate'
  | 'mondayOpen'
  | 'amend'
  | 'refresh'
  | 'kyc'
  | 'cancel';

export type DocumentAction = {
  key: ActionKey;
  label: string;
  enabled: boolean;
  reason: string | null;
};

export type ActionGroup = {
  group: 'Payment' | 'Document' | 'Change';
  actions: DocumentAction[];
};

export interface ProformaActionFacts extends InvoiceGateFacts {
  opportunityStatus?: string | null;
  opportunityId?: number | string | null;
  partNumber?: number | null;
}

const needs = (permission: string) => `Needs the ${permission} permission.`;

const SETTLED_MONEY =
  'Settled: no more money is recorded on it; a correction after settlement needs a credit note.';
const CANCELLED_MONEY =
  'Cancelled: nothing is collected on a cancelled proforma. The offer owner issues a new proforma.';
const CANCELLED_INSTALLMENT_MONEY =
  'Cancelled: nothing is collected on a cancelled proforma. The installment is issued again (see Change).';
const SETTLED_REPORT =
  'Settled: payment confirmations are added only while the proforma is Open.';
const COLLECTED_CHANGE =
  'Money has been collected: record the refund as a deduction first if it went back, or Settle.';
const SETTLED_CHANGE =
  'Settled: the document never changes again; a correction needs a credit note.';
const CANCELLED_CHANGE = 'Cancelled: a cancelled proforma never changes.';

const act = (
  key: ActionKey,
  label: string,
  reason: string | null,
): DocumentAction => ({ key, label, enabled: reason == null, reason });

function changeReason(
  status: string,
  allowed: boolean,
  mayWrite: boolean,
): string | null {
  if (allowed) return null;
  if (status === 'paid') return SETTLED_CHANGE;
  if (status === 'cancelled') return CANCELLED_CHANGE;
  if (status === 'issued' && mayWrite) return COLLECTED_CHANGE;
  return mayWrite ? 'Not available in this state.' : needs('invoices:write');
}

function moneyReason(
  status: string,
  mayMove: boolean,
  openOnly: boolean,
  laterInstallment = false,
): string | null {
  if (!payable(status)) {
    if (status === 'paid') return SETTLED_MONEY;
    return laterInstallment ? CANCELLED_INSTALLMENT_MONEY : CANCELLED_MONEY;
  }
  if (!mayMove) return needs('invoices:update-status');
  return openOnly ? null : 'Not available in this state.';
}

const IN_FLIGHT = ['queued', 'processing'];

export function proformaActions(
  invoice: ProformaActionFacts,
  permissions: readonly string[],
): ActionGroup[] {
  const gates = invoiceActionGates(invoice, permissions);
  const mayMove = holds(permissions, 'invoices:update-status');
  const mayWrite = holds(permissions, 'invoices:write');
  const maySend = holds(permissions, 'invoices:send');
  const status = invoice.status;
  const later = (invoice.partNumber ?? 0) > 1;

  const settleReason = gates.settle
    ? null
    : status === 'paid'
      ? 'Already Settled.'
      : status === 'cancelled'
        ? 'Cancelled: a cancelled proforma is never settled.'
        : !mayMove
          ? needs('invoices:update-status')
          : 'Record the money collected first: Settle needs money collected on the proforma.';

  const sendReason = gates.send.shown
    ? gates.send.blockedReason
    : status === 'cancelled'
      ? 'Cancelled: a cancelled proforma is never sent.'
      : invoice.offerId == null
        ? 'Not linked to an offer, so it has no client to send to.'
        : !maySend
          ? needs('invoices:send')
          : 'Not available in this state.';

  const busy = IN_FLIGHT.includes(invoice.opportunityStatus ?? '');
  const created =
    invoice.opportunityStatus === 'created' && invoice.opportunityId != null;
  const failed = invoice.opportunityStatus === 'failed';
  const mondayReason = busy
    ? 'Creating the opportunity: the queue is working on it.'
    : gates.mondayCreate
      ? null
      : !mayMove
        ? needs('invoices:update-status')
        : invoice.offerId == null
          ? 'Not linked to an offer.'
          : status === 'cancelled'
            ? 'Cancelled: no opportunity is made for a cancelled proforma.'
            : 'The opportunity is made once the proforma is Settled and its invoice issued.';

  const kycReason = gates.kyc
    ? null
    : !payable(status)
      ? 'KYC is started from a proforma only while it is Open.'
      : needs('invoices:write');

  return [
    {
      group: 'Payment',
      actions: [
        act(
          'recordPayment',
          'Record payment',
          gates.recordPayment
            ? null
            : moneyReason(status, mayMove, true, later),
        ),
        act(
          'addReport',
          'Add payment confirmation',
          gates.addReport
            ? null
            : status === 'paid'
              ? SETTLED_REPORT
              : moneyReason(status, mayMove, true, later),
        ),
        act('settle', 'Settle & issue invoice', settleReason),
      ],
    },
    {
      group: 'Document',
      actions: [
        act(
          'send',
          gates.send.shown && gates.send.retry ? 'Retry now' : 'Send to client',
          sendReason,
        ),
        act('download', 'Download PDF', null),
        act(
          'mondayCreate',
          failed
            ? 'Retry Monday opportunity'
            : created
              ? 'Create Monday opportunity again'
              : 'Create Monday opportunity',
          mondayReason,
        ),
        ...(invoice.opportunityId != null
          ? [act('mondayOpen', 'Open Monday opportunity', null)]
          : []),
      ],
    },
    {
      group: 'Change',
      actions: [
        act('amend', 'Amend', changeReason(status, gates.amend, mayWrite)),
        act(
          'refresh',
          'Refresh payer details',
          changeReason(status, gates.refreshBillTo, mayWrite),
        ),
        act('kyc', 'Start KYC for the payer', kycReason),
        act('cancel', 'Cancel', changeReason(status, gates.cancel, mayWrite)),
      ],
    },
  ];
}

/**
 * The action bar of a proforma (spec 9.5): fixed slots Record payment (its menu: Add client
 * payment report), Settle, Send, PDF, then the Change menu (Amend, Refresh payer details, Cancel).
 * Only the filled emphasis moves: Record payment while nothing is collected, Settle once
 * something is. What the user's permissions never allow is left out, and so is what the state
 * rules out for good (a Settled or Cancelled proforma takes no money and never changes); what
 * can still become possible stays, disabled, with its reason.
 */
export type ProformaBar = {
  payment: { record: DocumentAction; report: DocumentAction } | null;
  settle: DocumentAction | null;
  send: DocumentAction | null;
  primary: 'recordPayment' | 'settle' | null;
  change: DocumentAction[];
  /** The reason every Change item shares, shown once, while all of them are refused for it. */
  changeReason: string | null;
};

export function proformaBar(
  invoice: ProformaActionFacts,
  permissions: readonly string[],
): ProformaBar {
  const all = proformaActions(invoice, permissions).flatMap((g) => g.actions);
  const get = (key: ActionKey) => all.find((a) => a.key === key)!;
  const gates = invoiceActionGates(invoice, permissions);
  const open = payable(invoice.status);
  const mayMove = open && holds(permissions, 'invoices:update-status');
  const mayWrite = open && holds(permissions, 'invoices:write');
  const settle = mayMove ? get('settle') : null;
  const change = mayWrite
    ? (['amend', 'refresh', 'cancel'] as const).map(get)
    : [];
  const reasons = new Set(change.map((a) => a.reason));
  return {
    payment: mayMove
      ? { record: get('recordPayment'), report: get('addReport') }
      : null,
    settle,
    send: gates.send.shown ? get('send') : null,
    primary: settle?.enabled ? 'settle' : mayMove ? 'recordPayment' : null,
    change,
    changeReason:
      change.length > 0 && reasons.size === 1 ? [...reasons][0] : null,
  };
}

export function finalInvoiceActions(
  invoice: {
    sendBlockedReason: string | null;
    deliveryEmail: string | null;
    queued: boolean;
  },
  permissions: readonly string[],
): ActionGroup[] {
  const sendReason = !holds(permissions, 'invoices:send')
    ? needs('invoices:send')
    : invoice.sendBlockedReason;
  return [
    {
      group: 'Document',
      actions: [
        act(
          'send',
          invoice.queued
            ? 'Retry now'
            : `Send to ${invoice.deliveryEmail ?? 'client'}`,
          sendReason,
        ),
        act('download', 'Download PDF', null),
      ],
    },
  ];
}

export type CancelledWayForward =
  | { kind: 'replaced'; id: number; number: string }
  | { kind: 'new-proforma' }
  | null;

/**
 * The way forward from a cancelled proforma that is not a plan installment (installments are
 * issued again through `ReissueInstallment`): the proforma issued after it, or, while there is
 * none, Issue new proforma by the offer's owner.
 */
export function cancelledWayForward(
  invoice: {
    id: number;
    status: string;
    offerId: number | null;
    reissue?: unknown;
  },
  issueOrder: { id: number; number: string; status: string }[],
): CancelledWayForward {
  if (invoice.status !== 'cancelled' || invoice.offerId == null) return null;
  if (invoice.reissue) return null;
  const at = issueOrder.findIndex((p) => p.id === invoice.id);
  const next = issueOrder.slice(at + 1).find((p) => p.status !== 'cancelled');
  return next
    ? { kind: 'replaced', id: next.id, number: next.number }
    : { kind: 'new-proforma' };
}
